"use client"

import React, { useState, useEffect, forwardRef } from "react"
import { Input } from "@/components/ui/input"

export interface DebouncedInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> {
  value: string
  onChange: (value: string) => void
  debounceMs?: number
}

/**
 * DebouncedInput Component
 * 
 * A performance-optimized input that provides immediate visual feedback
 * while debouncing the actual onChange calls to prevent expensive re-renders.
 * 
 * @param value - The controlled value from parent component
 * @param onChange - Callback fired after debounce delay
 * @param debounceMs - Debounce delay in milliseconds (default: 300ms)
 * @param ...props - All other standard input props
 */
const DebouncedInput = forwardRef<HTMLInputElement, DebouncedInputProps>(
  ({ value, onChange, debounceMs = 300, className, ...props }, ref) => {
    // Internal state for immediate visual feedback
    const [localValue, setLocalValue] = useState(value)

    // Sync local state when external value changes (e.g., form data loads)
    useEffect(() => {
      setLocalValue(value)
    }, [value])

    // Debounce the onChange callback to prevent expensive parent re-renders
    useEffect(() => {
      // Only trigger onChange if the value actually changed
      if (localValue === value) return

      const timer = setTimeout(() => {
        onChange(localValue)
      }, debounceMs)

      return () => clearTimeout(timer)
    }, [localValue, value, onChange, debounceMs])

    return (
      <Input
        {...props}
        ref={ref}
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        className={className}
      />
    )
  }
)

DebouncedInput.displayName = "DebouncedInput"

export { DebouncedInput } 